import type { ZodTypeAny, infer as ZodInfer } from "zod/v4";

export type Method = "GET" | "POST" | "DELETE" | "PATCH" | "PUT";

type PathParamType = string | number;
type ExtractPathParams<
  Path extends string,
  Acc extends Record<string, string> = {},
> = Path extends `${infer _Start}:${infer Param}/${infer Rest}`
  ? ExtractPathParams<Rest, Acc & { [K in Param]: PathParamType }>
  : Path extends `${infer _Start}:${infer Param}`
    ? Acc & { [K in Param]: PathParamType }
    : Acc;

type IsAny<T> = 0 extends 1 & T ? true : false;

type Flatten<T> = T extends infer O ? { [K in keyof O]: O[K] } : never;

// Smart 'any' that hints path parameters but doesn't enforce them
type SmartAny<PathParams extends Record<string, any>> =
  PathParams extends Record<string, never>
    ? any // No path params, just return any
    : Partial<PathParams> & Record<string, any>; // Optional path param hints + accept anything

type WithPathParams<ReqType, Path extends string> =
  ExtractPathParams<Path> extends Record<string, never>
    ? ReqType
    : IsAny<ReqType> extends true
      ? SmartAny<ExtractPathParams<Path>> // Smart any with path param hints
      : ReqType extends Record<string, never>
        ? Flatten<ExtractPathParams<Path>> // If ReqType is empty object, use only path params
        : Omit<ExtractPathParams<Path>, keyof ReqType> & ReqType; // Remove conflicts, requestType wins

export interface EndpointDef<
  ReqSchema extends ZodTypeAny = ZodTypeAny,
  ResSchema extends ZodTypeAny = ZodTypeAny,
  ReqType = ZodInfer<ReqSchema>,
  ResType = ZodInfer<ResSchema>,
  Path extends string = string,
> {
  method: Method;
  path: Path;
  request: ReqSchema;
  response: ResSchema;
  requestType: WithPathParams<ReqType, Path>;
  responseType: ResType;
  description?: string;
}

export type EndpointShape = {
  method: Method;
  path: string;
  request: ZodTypeAny;
  response: ZodTypeAny;
  requestType: unknown;
  responseType: unknown;
  description?: string;
};

export type EndpointRegistry = Readonly<Record<string, EndpointShape>>;

// Helper type to preserve endpoint type information while satisfying registry constraint
export type PreserveEndpointTypes<T extends Record<string, EndpointShape>> = {
  readonly [K in keyof T]: T[K] extends EndpointDef<
    infer Req,
    infer Res,
    infer ReqType,
    infer ResType
  >
    ? EndpointDef<Req, Res, ReqType, ResType>
    : T[K];
};

type BuildFullPath<Base extends readonly string[], Path extends string> = Base extends readonly []
  ? Path
  : Base extends readonly [infer First, ...infer Rest]
    ? First extends string
      ? Rest extends readonly string[]
        ? Path extends ""
          ? BuildFullPath<Rest, First>
          : BuildFullPath<Rest, `${First}/${Path}`>
        : never
      : never
    : never;

export const createEndpointBase =
  <BasePath extends readonly string[]>(...pathSegments: BasePath) =>
  <
    ReqSchema extends ZodTypeAny,
    ResSchema extends ZodTypeAny,
    ReqType = ZodInfer<ReqSchema>,
    ResType = ZodInfer<ResSchema>,
    Path extends string = string,
  >(def: {
    method: Method;
    path: Path;
    request: ReqSchema;
    response: ResSchema;
    requestType?: ReqType;
    responseType?: ResType;
    description?: string;
  }): EndpointDef<
    ReqSchema,
    ResSchema,
    WithPathParams<ReqType, BuildFullPath<BasePath, Path>>,
    ResType,
    BuildFullPath<BasePath, Path>
  > => {
    const basePath = pathSegments.join("/");
    const fullPath = def.path ? (basePath ? `${basePath}/${def.path}` : def.path) : basePath;

    // Create the combined request type that includes path parameters
    // If no requestType is provided, use an empty object instead of falling back to the Zod inferred type
    const baseRequestType = def.requestType ?? ({} as ReqType);

    return {
      ...def,
      path: fullPath,
      description: def.description,
      requestType: baseRequestType as WithPathParams<ReqType, BuildFullPath<BasePath, Path>>,
      responseType: (def.responseType ?? (undefined as unknown as ResType)) as ResType,
    } as EndpointDef<
      ReqSchema,
      ResSchema,
      WithPathParams<ReqType, BuildFullPath<BasePath, Path>>,
      ResType,
      BuildFullPath<BasePath, Path>
    >;
  };
